import React, { useState, useEffect } from 'react';
import { Transaction } from '../../types';
import Text from '../Text/Text';
import { getTrackingStatus, isTrackingExpired } from '../../utils/timeCalculations';
import './TrackingStatusBanner.css';

interface TrackingStatusBannerProps {
  transaction: Transaction;
  userRole: 'buyer' | 'seller';
}

/**
 * TrackingStatusBanner component
 * 
 * Displays a prominent banner at the top of the ReleaseFundsScreen for buyers
 * when tracking information is pending or the tracking window has expired.
 * 
 * Two scenarios:
 * 1. Pending: Shows countdown timer with remaining time for seller to provide tracking
 * 2. Expired: Shows static message that tracking window has expired and transaction will be cancelled
 */
export const TrackingStatusBanner: React.FC<TrackingStatusBannerProps> = ({
  transaction,
  userRole
}) => {
  const [trackingStatus, setTrackingStatus] = useState(getTrackingStatus(transaction, { shortFormat: false }));

  // Update tracking status
  useEffect(() => {
    const updateBannerState = () => {
      const newStatus = getTrackingStatus(transaction, {
        shortFormat: false,
        showExpiredText: true
      });
      setTrackingStatus(newStatus);
    };

    // Initial update
    updateBannerState();

    // Set up interval for real-time updates (every second like CountdownTimer)
    const intervalId = setInterval(updateBannerState, 1000);

    // Cleanup interval on unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [transaction]);

  // Only show banner for buyers with in_escrow transactions without tracking
  if (userRole !== 'buyer' ||
      !transaction ||
      transaction.status !== 'in_escrow' ||
      transaction.trackingId) {
    return null;
  }

  const renderPendingTrackingBanner = () => {
    return (
      <div className="tracking-status-banner tracking-status-banner--pending">
        <div className="tracking-status-banner__icon">
          ⏱️
        </div>
        <div className="tracking-status-banner__content">
          <Text className="tracking-status-banner__title text-body-bold">
            Tracking Information Pending
          </Text>
          <Text className="tracking-status-banner__message text-body">
            The seller has <strong>{trackingStatus.displayText}</strong> remaining to provide tracking details for your order.
          </Text>
        </div>
      </div>
    );
  };

  const renderExpiredTrackingBanner = () => {
    return (
      <div className="tracking-status-banner tracking-status-banner--expired">
        <div className="tracking-status-banner__icon">
          ⚠️
        </div>
        <div className="tracking-status-banner__content">
          <Text className="tracking-status-banner__title text-body-bold">
            Tracking Information Not Provided
          </Text>
          <Text className="tracking-status-banner__message text-body">
            The seller failed to provide tracking information within the required timeframe.
            This transaction is now pending cancellation and funds will be automatically
            returned to your original payment method.
          </Text>
        </div>
      </div>
    );
  };

  // Render appropriate banner based on tracking status
  if (trackingStatus.isExpired) {
    return renderExpiredTrackingBanner();
  } else if (trackingStatus.needsTracking) {
    return renderPendingTrackingBanner();
  } else {
    return null;
  }
};

export default TrackingStatusBanner;